// src/components/layout/Navbar.jsx
//
// Sidebar — Direction A: 232px black panel with 3px cyan top stripe,
// gold accent on the active item, gold badges for renewals/tickets,
// gold initials avatar for the user card, theme toggle in the header.
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Box, Tooltip, IconButton } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EventIcon from "@mui/icons-material/Event";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import Logo from "../brand/Logo";
import { useAuth } from "../../context/AuthContext";
import BusinessIcon from "@mui/icons-material/Business";
import { hasFinanceAccess, hasSupplierAccess } from "../../utils/roleAccess";
import { useThemeContext } from "../../context/ThemeContext";
import { tokens, fonts } from "../../theme/tokens";
import { getTicketStats, TICKET_STATS_CHANGED_EVENT } from "../../api/ticketApi";

const SIDEBAR_BG = tokens.black;
const STRIPE = tokens.cyan;
const GOLD = tokens.gold;
const INACTIVE = "rgba(255,255,255,0.6)";

const initialsFor = (user) => {
  if (!user) return "U";
  if (user.name) {
    return user.name
      .split(/\s+/)
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  if (user.username) return user.username[0].toUpperCase();
  return "U";
};

const NAV_ITEMS = [
  { key: "home", label: "Dashboard", icon: HomeIcon, path: "/dashboard" },
  // Keep sidebar highlight for property-scoped flows: list/details/metadata, unit pages,
  // and LPE1 questionnaires opened from Property Enquiries (routes are /lpe1/..., not /properties/...).
  {
    key: "properties",
    label: "Properties",
    icon: ApartmentIcon,
    path: "/properties",
    activePrefixes: ["/properties", "/units", "/lpe1"],
  },
  { key: "finance", label: "Finance", icon: AccountBalanceIcon, path: "/finance" },
  { key: "renewals", label: "Renewals", icon: EventIcon, path: "/renewals", badgeKey: "expiring_soon" },
  { key: "documents", label: "Documents", icon: DescriptionIcon, path: "/documents" },
  { key: "unit-transfers", label: "Unit Transfers", icon: SwapHorizIcon, path: "/unit-transfers" },
  { key: "tickets", label: "Tickets", icon: ConfirmationNumberIcon, path: "/tickets?summary=open", matchPath: "/tickets", badgeKey: "open_tickets" },
  { key: "suppliers", label: "Suppliers", icon: BusinessIcon, path: "/suppliers" },
  { key: "feedback", label: "Feedback", icon: FeedbackOutlinedIcon, path: "/feedback" },
  { key: "search", label: "Search", icon: SearchIcon, path: "/search" },
  { key: "settings", label: "Settings", icon: SettingsIcon, path: "/settings" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, roleCapabilities } = useAuth();
  const { mode, toggleTheme } = useThemeContext();
  const [badges, setBadges] = useState({ open_tickets: 0, expiring_soon: 0 });

  useEffect(() => {
    let cancelled = false;
    const refreshOpenTicketsBadge = async () => {
      try {
        const stats = await getTicketStats();
        if (cancelled) return;
        const active = (stats?.open ?? 0) + (stats?.in_progress ?? 0);
        setBadges((b) => ({ ...b, open_tickets: active }));
      } catch {
        // Quiet — sidebar still renders without the badge.
      }
    };
    refreshOpenTicketsBadge();
    const onTicketStatsChanged = () => {
      refreshOpenTicketsBadge();
    };
    window.addEventListener(TICKET_STATS_CHANGED_EVENT, onTicketStatsChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(TICKET_STATS_CHANGED_EVENT, onTicketStatsChanged);
    };
  }, []);

  const isActive = (item) => {
    const prefixes = item.activePrefixes ?? [item.matchPath || item.path.split("?")[0]];
    return prefixes.some((prefix) => {
      if (!prefix) return false;
      return location.pathname === prefix || location.pathname.startsWith(`${prefix}/`);
    });
  };

  const userRoles = user?.claims_role || user?.roles || [];
  const canAccessFinance = hasFinanceAccess(userRoles, roleCapabilities);
  const navItems = NAV_ITEMS.filter((item) => (item.key !== "finance" || canAccessFinance) && (item.key !== "suppliers" || hasSupplierAccess(userRoles, roleCapabilities)));

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: SIDEBAR_BG,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* 3px cyan top stripe */}
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, backgroundColor: STRIPE }} />

      {/* Header — logo + theme toggle */}
      <Box
        sx={{
          px: 2.5,
          pt: "26px",
          pb: 2.25,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ cursor: "pointer" }} onClick={() => navigate("/dashboard")}>
          <Logo dark />
        </Box>
        <Tooltip title={mode === "light" ? "Switch to dark" : "Switch to light"}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              width: 30,
              height: 30,
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.08)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
            }}
          >
            {mode === "light" ? <DarkModeIcon sx={{ fontSize: 14 }} /> : <LightModeIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Nav */}
      <Box
        component="nav"
        sx={{ flex: 1, p: "14px 12px", display: "flex", flexDirection: "column", gap: "1px", overflowY: "auto" }}
      >
        {navItems.map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
          return (
            <Box
              key={item.key}
              component={Link}
              to={item.path}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: "6px",
                cursor: "pointer",
                color: active ? "#fff" : INACTIVE,
                backgroundColor: active ? "rgba(255,255,255,0.07)" : "transparent",
                fontSize: 13.5,
                fontWeight: active ? 600 : 500,
                fontFamily: fonts.sans,
                textDecoration: "none",
                position: "relative",
                transition: "background .12s, color .12s",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: active ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)",
                },
              }}
            >
              {active && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    backgroundColor: GOLD,
                    borderRadius: "2px",
                  }}
                />
              )}
              <Icon
                sx={{
                  fontSize: 18,
                  color: active ? GOLD : INACTIVE,
                }}
              />
              <Box sx={{ flex: 1 }}>{item.label}</Box>
              {badgeCount > 0 && (
                <Box
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    px: "7px",
                    py: "1px",
                    borderRadius: "8px",
                    backgroundColor: active ? GOLD : "rgba(255,255,255,0.12)",
                    color: active ? tokens.black : "rgba(255,255,255,0.85)",
                  }}
                >
                  {badgeCount}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* User card */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
        }}
        onClick={() => navigate("/profile")}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            backgroundColor: GOLD,
            color: tokens.black,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            fontFamily: fonts.serif,
            flexShrink: 0,
          }}
        >
          {initialsFor(user)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.name || user?.username || "User"}
          </Box>
          <Box
            sx={{
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              mt: "2px",
            }}
          >
            {user?.claims_role?.[0] || user?.roles?.[0] || "Manager"}
          </Box>
        </Box>
        <ChevronRightIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }} />
      </Box>
    </Box>
  );
}
